import type { ReactElement } from 'react';
import { Chip, cn, useI18n } from '@xdocs/ui';
import { resolveCategoryLabel } from './categoryLabel.js';
import { useSearchT } from './useSearchT.js';
import styles from './CategoryFilterChips.module.css';

export interface CategoryFilterChipsProps {
  /** The FULL category catalog (`useCategoryCatalog` → `category.list`, §5.9)
   * — never derive this from the currently loaded `search.query` result page:
   * that facet self-eliminates (selecting a category narrows `rows`, which
   * then narrows the chip set built from those same `rows`) and drops any
   * category with zero hits on the loaded page. */
  categories: readonly { id: string; slug: string; nameKey: string }[];
  activeCategoryId: string | null;
  onSelect: (categoryId: string | null) => void;
}

/** F31 category filter chips (F8) — see `categoryLabel.ts` for the dynamic
 * `nameKey` resolution/fallback contract. */
export function CategoryFilterChips({ categories, activeCategoryId, onSelect }: CategoryFilterChipsProps): ReactElement | null {
  const { t } = useI18n();
  const { t: tSearch } = useSearchT();

  if (categories.length === 0) return null;

  return (
    <div role="group" aria-label={tSearch('search:filters.categories.label')} className={cn(styles['group'])}>
      <Chip active={activeCategoryId === null} onClick={() => onSelect(null)}>
        {tSearch('search:filters.categories.all')}
      </Chip>
      {categories.map((category) => (
        <Chip key={category.id} active={activeCategoryId === category.id} onClick={() => onSelect(category.id)}>
          {resolveCategoryLabel(t, category)}
        </Chip>
      ))}
    </div>
  );
}
